/**
 * Small display helpers for directory listings. Pure: no i18n, no clock.
 */
import type { HoursMode, Listing, ListingMessage, OpeningHoursResult } from '../types';

/** The text in the current language, falling back to the other one (plan 13). */
export function pickLang(en: string | undefined, he: string | undefined, isHebrew: boolean): string {
  return (isHebrew ? he || en : en || he) ?? '';
}

/**
 * Street only, no house number, for listings whose owner switched off
 * "show exact location" (plan 4.2). "Nahal Dolev 12" → "Nahal Dolev".
 */
export function approximateAddress(address: string | undefined): string {
  return (address ?? '')
    .split(/\s+/)
    .filter((word) => !/\d/.test(word))
    .join(' ')
    .replace(/[\s,]+$/, '')
    .trim();
}

/** The address to show, in the current language, respecting the privacy toggle. */
export function displayAddress(listing: Pick<Listing, 'location'>, isHebrew: boolean): string {
  const loc = listing.location;
  if (!loc) return '';
  const address = pickLang(loc.addressEn, loc.addressHe, isHebrew);
  return loc.showExactLocation === false ? approximateAddress(address) : address;
}

/** Messages showing right now (started, not yet expired), newest first. `nowIso` is an ISO timestamp. */
export function activeMessages(messages: ListingMessage[] | undefined, nowIso: string): ListingMessage[] {
  const now = Date.parse(nowIso);
  return (messages ?? [])
    .filter((m) => Date.parse(m.startsAt) <= now && now < Date.parse(m.expiresAt))
    .sort((a, b) => Date.parse(b.startsAt) - Date.parse(a.startsAt));
}

/** Most messages the "Latest messages" strip shows. */
export const LATEST_MESSAGES_MAX = 20;

export interface LatestMessage {
  listing: Listing;
  message: ListingMessage;
}

/**
 * "Latest messages" strip on the directory pages (plan 4.5, docs/phase-5-plan.md):
 * active messages of published listings (every category, shuls too), newest
 * first, at most `max`.
 */
export function latestMessages(listings: Listing[], nowIso: string, max = LATEST_MESSAGES_MAX): LatestMessage[] {
  return listings
    .filter((listing) => listing.status === 'published')
    .flatMap((listing) => activeMessages(listing.messages, nowIso).map((message) => ({ listing, message })))
    .filter(({ message }) => (message.textHe ?? '').trim() || (message.textEn ?? '').trim())
    .sort((a, b) => Date.parse(b.message.startsAt) - Date.parse(a.message.startsAt))
    .slice(0, max);
}

/** Phone of the first contact that has one (gets the big Call button). */
export function primaryPhone(listing: Pick<Listing, 'contacts'>): string | undefined {
  return listing.contacts?.find((c) => c.phone)?.phone;
}

/** wa.me wants digits only, with the country code: 050-1234567 → 972501234567. */
export function whatsappLink(number: string): string {
  let digits = number.replace(/\D/g, '');
  if (digits.startsWith('0')) digits = `972${digits.slice(1)}`;
  return `https://wa.me/${digits}`;
}

export function telLink(phone: string): string {
  return `tel:${phone.replace(/[^\d+*#]/g, '')}`;
}

// ---------- Status badge ----------

export type BadgeTone = 'open' | 'soon' | 'closed' | 'neutral';

/**
 * What the status badge says: an i18n key under `directory.` (e.g.
 * `openStatus.openUntil`) plus values; the component translates it.
 * Text is always shown with the color, never color alone (plan 13).
 */
export interface StatusBadge {
  tone: BadgeTone;
  key: string;
  values?: Record<string, string>;
  /** Day of week (0–6) for "Opens Sun 20:00"; the component turns it into a name. */
  dayOfWeek?: number;
  /** Add "(next day)" after the time. */
  nextDay?: boolean;
  /** Prefix with "Closed ·". */
  closedPrefix?: boolean;
}

function dayOfWeekOfDate(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay();
}

export function statusBadge(
  listing: { hoursMode: HoursMode; hours: unknown[] },
  result: OpeningHoursResult | undefined
): StatusBadge | null {
  if (listing.hoursMode === 'byAppointment' || listing.hoursMode === 'callFirst') {
    return { tone: 'neutral', key: `hoursMode.${listing.hoursMode}` };
  }
  if (listing.hoursMode === 'alwaysOpen') return { tone: 'open', key: 'hoursMode.alwaysOpen' };
  if (!result) return null;
  if (!listing.hours?.length) return { tone: 'neutral', key: 'openStatus.noHours' };

  const today = result.today.date;
  switch (result.status) {
    case 'open':
    case 'closesSoon': {
      if (!result.closesAt) return { tone: 'open', key: 'openStatus.open' };
      return {
        tone: result.status === 'open' ? 'open' : 'soon',
        key: result.status === 'open' ? 'openStatus.openUntil' : 'openStatus.closesAt',
        values: { time: result.closesAt.time },
        nextDay: result.closesAt.date !== today,
      };
    }
    case 'opensSoon':
      return result.nextOpening
        ? { tone: 'soon', key: 'openStatus.opensAt', values: { time: result.nextOpening.time } }
        : { tone: 'soon', key: 'openStatus.opensSoon' };
    case 'callToCheck':
      return { tone: 'neutral', key: 'openStatus.callToCheck' };
    case 'closed':
    default: {
      const next = result.nextOpening;
      if (!next) return { tone: 'closed', key: 'openStatus.closed' };
      if (next.date === today) {
        return { tone: 'closed', key: 'openStatus.opensAt', values: { time: next.time }, closedPrefix: true };
      }
      return {
        tone: 'closed',
        key: 'openStatus.opensOn',
        values: { time: next.time },
        dayOfWeek: dayOfWeekOfDate(next.date),
        closedPrefix: true,
      };
    }
  }
}

/**
 * The subcategory chip text. A shiur with topic "Other" shows the owner's own
 * topic instead (Yoel, 2026-10-22). `t` is i18next's translate function.
 */
export function subcategoryLabel(
  listing: Listing,
  t: (key: string) => string,
  isHebrew: boolean
): string {
  if (listing.category === 'shiur' && listing.subcategory === 'other') {
    const own = pickLang(listing.details?.topicOtherEn, listing.details?.topicOtherHe, isHebrew);
    if (own) return own;
  }
  const sub = 'subcategory' in listing ? listing.subcategory : undefined;
  return sub ? t(`directory.sub.${sub}`) : t(`directory.category.${listing.category}`);
}
